import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';

const typeColors: Record<string, 'primary' | 'success' | 'info' | 'warning' | 'default'> = {
  '저수지': 'primary',
  '평지': 'success',
  '계곡': 'info',
  '바다': 'warning',
  '기타': 'default',
};

const FreshwaterSpotList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [spots, setSpots] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    loadSpots();
    loadStats();
  }, [page, rowsPerPage, typeFilter]);

  const loadSpots = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'name',
        sortOrder: 'asc',
      };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;

      const data = await api.getFreshwaterSpots(params);
      setSpots(data.data);
      setTotal(data.total);
    } catch (e: any) {
      setError(e.message || '로딩 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await api.getFreshwaterSpotStats();
      setStats(data);
    } catch {}
  };

  const handleSearch = () => {
    setPage(0);
    loadSpots();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.deleteFreshwaterSpot(deleteId);
      setDeleteId(null);
      loadSpots();
      loadStats();
    } catch (e: any) {
      setError(e.message || '삭제 실패');
    }
  };

  const handleViewDetail = async (id: number) => {
    try {
      const data = await api.getFreshwaterSpot(id);
      setSelectedSpot(data);
      setDetailOpen(true);
    } catch {}
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        민물낚시터 관리
      </Typography>

      {/* 통계 카드 */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="primary">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">전체</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="success.main">{stats.withCoords}</Typography>
                <Typography variant="body2" color="text.secondary">좌표 있음</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="info.main">{stats.withImage}</Typography>
                <Typography variant="body2" color="text.secondary">이미지 있음</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                {stats.byType?.map((t: any) => (
                  <Chip
                    key={t.type}
                    label={`${t.type}: ${t.count}`}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 검색/필터 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            label="이름/주소/어종 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel>유형</InputLabel>
            <Select value={typeFilter} label="유형" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">전체</MenuItem>
              {['저수지', '평지', '계곡', '기타', '바다'].map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handleSearch}><SearchIcon /></IconButton>
            <IconButton onClick={() => { setSearch(''); setTypeFilter(''); setPage(0); loadSpots(); }}><RefreshIcon /></IconButton>
          </Stack>
        </Stack>
      </Paper>

      {/* 모바일: 카드 리스트 */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : spots.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">낚시터가 없습니다</Typography>
            </Paper>
          ) : (
            spots.map((spot) => (
              <Card key={spot.id} sx={{ cursor: 'pointer' }} onClick={() => handleViewDetail(spot.id)}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        #{spot.id} · {spot.latitude ? '📍 좌표있음' : '좌표없음'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {spot.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={spot.type}
                          size="small"
                          color={typeColors[spot.type] || 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                        {spot.mainSpecies && (
                          <Typography variant="caption" color="text.secondary">
                            {spot.mainSpecies}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {spot.roadAddress || spot.lotAddress || '-'}
                      </Typography>
                    </Box>
                    <Stack direction="column" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                      <IconButton size="small" color="error" onClick={() => setDeleteId(spot.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage=""
          />
        </Stack>
      ) : (
      /* 데스크탑: 테이블 */
      <TableContainer component={Paper}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>유형</TableCell>
                <TableCell>낚시터명</TableCell>
                <TableCell>주소</TableCell>
                <TableCell>주요어종</TableCell>
                <TableCell>좌표</TableCell>
                <TableCell align="right">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {spots.map((spot) => (
                <TableRow key={spot.id} hover>
                  <TableCell>{spot.id}</TableCell>
                  <TableCell>
                    <Chip
                      label={spot.type}
                      size="small"
                      color={typeColors[spot.type] || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {spot.name}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {spot.roadAddress || spot.lotAddress || '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {spot.mainSpecies || '-'}
                  </TableCell>
                  <TableCell>
                    {spot.latitude ? '✅' : '❌'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleViewDetail(spot.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleteId(spot.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>
      )}

      {/* 상세 다이얼로그 */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{selectedSpot?.name}</DialogTitle>
        <DialogContent dividers>
          {selectedSpot && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
              {[
                ['유형', selectedSpot.type],
                ['도로명주소', selectedSpot.roadAddress],
                ['지번주소', selectedSpot.lotAddress],
                ['전화번호', selectedSpot.phone],
                ['주요어종', selectedSpot.mainSpecies],
                ['수면적', selectedSpot.waterArea ? `${selectedSpot.waterArea}㎡` : null],
                ['최대수용', selectedSpot.maxCapacity ? `${selectedSpot.maxCapacity}명` : null],
                ['시설유형', selectedSpot.facilityType],
                ['이용요금', selectedSpot.fee],
                ['주요포인트', selectedSpot.mainPoints],
                ['안전시설', selectedSpot.safetyFacilities],
                ['편의시설', selectedSpot.convenienceFacilities],
                ['주변관광지', selectedSpot.nearbyAttractions],
                ['관리기관', selectedSpot.managementOrg],
                ['관리기관전화', selectedSpot.managementPhone],
                ['위도', selectedSpot.latitude],
                ['경도', selectedSpot.longitude],
                ['관리번호', selectedSpot.managementId],
              ]
                .filter(([, v]) => v != null && v !== '')
                .map(([label, value]) => (
                  <React.Fragment key={label as string}>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold">
                      {label}
                    </Typography>
                    <Typography variant="body2">{String(value)}</Typography>
                  </React.Fragment>
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} fullScreen={isMobile}>
        <DialogTitle>삭제 확인</DialogTitle>
        <DialogContent>이 낚시터를 삭제하시겠습니까?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>취소</Button>
          <Button color="error" onClick={handleDelete}>삭제</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FreshwaterSpotList;
